// Dashboard panel wrapping the force graph: legend, selection detail, and the
// honest caveat about version collapsing.
//
// The map can be expanded to fill the screen — a tenant with a governed repo
// or two quickly outgrows a 520px card, and labels start to collide. The
// expanded view is the same graph and the same selection, just bigger.

import { useQuery } from "@tanstack/react-query";
import { Copy, Maximize2, Minimize2 } from "lucide-react";
import * as React from "react";

import {
  ForceGraph,
  KIND_LABELS,
  NodeShape,
  isRepoSide,
  styleFor,
} from "@/components/graph/force-graph";
import { nodePath } from "@/components/graph/node-path";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { getTenantGraph } from "@/lib/api/endpoints";
import type { GraphNode, GraphResponse } from "@/types/api";

// ---- Panel -------------------------------------------------------------------

export function GraphPanel() {
  const graph = useQuery({ queryKey: ["tenant", "graph"], queryFn: getTenantGraph });
  const [selected, setSelected] = React.useState<GraphNode | null>(null);
  const [expanded, setExpanded] = React.useState(false);

  // Esc closes the expanded view, and the page behind it must not scroll.
  React.useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpanded(false);
    };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [expanded]);

  if (graph.isLoading) return <Skeleton className="h-[560px] w-full" />;
  if (graph.isError) return <ErrorState error={graph.error} onRetry={() => void graph.refetch()} />;

  const data = graph.data!;
  // The tenant node is always present, so "nothing configured" means one node.
  if (data.nodes.length <= 1) {
    return (
      <EmptyState
        title="Nothing to map yet"
        hint="Create a domain and an agent, and this becomes a picture of how everything connects."
      />
    );
  }

  const hasRepos = data.nodes.some((n) => isRepoSide(n.kind));
  const notDrawn = data.nodes.reduce(
    (sum, n) => sum + (n.kind === "github_repo" ? Number(n.meta?.files_not_drawn ?? 0) : 0),
    0,
  );

  const expandButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setExpanded((v) => !v)}
      aria-label={expanded ? "Exit full screen" : "Expand map"}
      title={expanded ? "Exit full screen (Esc)" : "Expand map"}
    >
      {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      {expanded ? "Close" : "Expand"}
    </Button>
  );

  if (expanded) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Configuration map"
        className="fixed inset-0 z-50 flex flex-col gap-3 bg-background p-4"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">Configuration map</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {data.nodes.length} nodes · {data.edges.length} links. Drag to rearrange, click to
              trace. Esc to close.
            </p>
          </div>
          <div className="flex items-start gap-4">
            <Legend data={data} />
            {expandButton}
          </div>
        </div>
        <div className="min-h-0 flex-1 rounded-md border border-border bg-muted/20">
          <ForceGraph
            data={data}
            selected={selected?.id ?? null}
            onSelect={setSelected}
            className="h-full w-full"
          />
        </div>
        {/* Fixed height, whether or not something is selected: the map takes
            whatever space is left, so a detail panel that came and went
            resized the map and moved every node out from under the cursor. */}
        <div className="h-32 shrink-0 overflow-y-auto">
          <SelectionDetail data={data} selected={selected} />
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Configuration map</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            {data.nodes.length} nodes · {data.edges.length} links. Drag to rearrange, click to trace
            what something is wired to.
          </p>
        </div>
        <div className="flex items-start gap-4">
          <Legend data={data} />
          {expandButton}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="rounded-md border border-border bg-muted/20">
          <ForceGraph data={data} selected={selected?.id ?? null} onSelect={setSelected} />
        </div>

        <SelectionDetail data={data} selected={selected} />

        {data.collapsed_to_latest_version ? (
          // Say it rather than let the picture imply completeness: skills,
          // tools and knowledge bases attach to a *version*, and drawing every
          // version of every agent would be unreadable.
          <p className="text-xs text-muted-foreground">
            Each agent shows its <code className="font-mono">latest</code> version. Other versions
            and what they attach to are not drawn.
          </p>
        ) : null}

        {hasRepos ? (
          // Where the right-hand side comes from, and how fresh it is.
          <p className="text-xs text-muted-foreground">
            GitHub repositories, on the right, show the Claude Code and Cursor agents, skills,
            commands and rules found on each governed repo&apos;s default branch at its last sync —
            a skill is one node for its whole folder. Settings, hooks and MCP config are on the Repo
            Governance page.
            {notDrawn > 0 ? ` ${notDrawn} more files are not drawn to keep the map readable.` : ""}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Only the kinds actually present — a legend full of absent things makes a
 * small tenant look broken. Ordered as KIND_LABELS lists them, so the Aviary
 * kinds come first and the GitHub ones after. */
function Legend({ data }: { data: GraphResponse }) {
  const present = new Set(data.nodes.map((n) => n.kind));
  const order = Object.keys(KIND_LABELS);
  const kinds = Array.from(present).sort(
    (a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99),
  );
  return (
    <div className="flex flex-wrap justify-end gap-x-3 gap-y-1">
      {kinds.map((kind) => {
        const s = styleFor(kind);
        return (
          <span key={kind} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <svg viewBox="-9 -9 18 18" className="h-3.5 w-3.5 shrink-0" aria-hidden>
              <NodeShape shape={s.shape} r={6} fill={s.fill} />
            </svg>
            {KIND_LABELS[kind] ?? kind}
          </span>
        );
      })}
    </div>
  );
}

function SelectionDetail({ data, selected }: { data: GraphResponse; selected: GraphNode | null }) {
  if (!selected) {
    return (
      <p className="text-xs text-muted-foreground">
        Click a node to see its details, its path, and what it connects to.
      </p>
    );
  }

  const path = nodePath(data, selected.id);
  const copy = () => {
    void navigator.clipboard
      .writeText(path)
      .then(() => toast.success("Path copied."))
      .catch(() => toast.error("Could not copy the path."));
  };

  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-center gap-2">
        <Badge variant="outline">{KIND_LABELS[selected.kind] ?? selected.kind}</Badge>
        <span className="text-sm font-medium">{selected.label}</span>
        {selected.sublabel ? (
          <span className="font-mono text-xs text-muted-foreground">{selected.sublabel}</span>
        ) : null}
      </div>
      {selected.meta && Object.keys(selected.meta).length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {Object.entries(selected.meta)
            .filter(([, v]) => v !== null && v !== undefined && v !== "")
            .map(([k, v]) => (
              <span key={k}>
                {k.replace(/_/g, " ")} <span className="text-foreground">{String(v)}</span>
              </span>
            ))}
        </div>
      ) : null}
      {/* Where this node lives, like `pwd`. */}
      <div className="mt-3 flex items-center gap-2 rounded-md bg-muted/50 px-2 py-1.5">
        <span className="shrink-0 select-none font-mono text-xs text-muted-foreground">$ pwd</span>
        <code className="min-w-0 flex-1 break-all font-mono text-xs text-foreground">{path}</code>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={copy}
          aria-label="Copy path"
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
